/**
 * Orders screen (M1) — /app/orders, visible only when the 'orders' module is
 * enabled for the tenant. Lists purchase orders (KeHE invoice-ingested +
 * manual) with expandable line detail. Same visual conventions as FeedPage.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ChevronDown, ChevronUp, ChevronsUpDown, CreditCard, Plus, Search } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../design';
import {
  chargeKindLabel,
  chargeRuleTotal,
  defaultTreatmentForKind,
  landedSummaryLine,
  mostCommonVendor,
  nextPoStatus,
  orderTotalCents,
  searchPurchaseOrders,
  signedAmountForKind,
  sortPurchaseOrders,
  toValidPoLines,
  useAdvancePoStatus,
  useApplyChargeRule,
  useCreateLotReturn,
  useClearReviewForPoLine,
  useCreatePoCharge,
  useCreatePurchaseOrder,
  useDeleteLotReturn,
  useDeletePoCharge,
  useOrderSearchLines,
  usePoCharges,
  usePurchaseOrder,
  usePurchaseOrders,
  vendorInvoiceOptions,
  type OrderSortDirection,
  type OrderSortKey,
  type PoLineDraft,
} from '../../lib/orders';
import {
  claimDeadlineState,
  compareClaimDeadlines,
  useCreditClaims,
  useUpdateClaimStatus,
} from '../../lib/claims';
import { useMe, useModuleEnabled } from '../../lib/auth';
import { ApiCallError } from '../../lib/api';
import { formatFinanceDate, useCogsReplanState } from '../../lib/finance-summary';
import { AppShell } from '../../components/AppShell';
import { ProductThumb } from '../../components/ProductThumb';
import { ProductLink } from '../../components/ProductLink';
import { Skeleton, SkeletonLine } from '../../components/Skeleton';
import {
  PoChargeKind,
  PoChargeTreatment,
  type ApplyChargeRuleResponse,
  type ApplyChargeRuleRow,
  type LotReturn,
  type PoCharge,
  type PoLot,
  type PurchaseOrderLine,
  type PurchaseOrderSummary,
} from '@sourcito/schemas';

const STATUS_BADGE: Record<
  PurchaseOrderSummary['status'],
  { label: string; variant: 'neutral' | 'accent' | 'info' | 'warning' | 'positive' }
> = {
  placed: { label: 'Placed', variant: 'neutral' },
  confirmed: { label: 'Confirmed', variant: 'accent' },
  in_transit: { label: 'In transit', variant: 'info' },
  received_partial: { label: 'Partially received', variant: 'warning' },
  closed: { label: 'Closed', variant: 'positive' },
};

const money = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export function OrdersPage() {
  const enabled = useModuleEnabled('orders');
  const warehouseEnabled = useModuleEnabled('warehouse');
  const { data: orders, isLoading, isError } = usePurchaseOrders(enabled);
  const { data: me } = useMe();
  const isOwner = me?.user.role === 'owner';
  // R2 item 9 fix pass I7: clearing a review hold is owner/operator only,
  // same bar the route enforces (ownerOrOperatorOrForbidden).
  const canReview = me?.user.role === 'owner' || me?.user.role === 'operator';
  const [openId, setOpenId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  // null = default order as returned by the API — no column sort applied yet.
  const [sort, setSort] = useState<{ key: OrderSortKey; direction: OrderSortDirection } | null>(
    null
  );

  const [creating, setCreating] = useState(false);
  const [applyingRule, setApplyingRule] = useState(false);

  const orderIds = useMemo(() => (orders ?? []).map((o) => o.id), [orders]);
  const searchLines = useOrderSearchLines(orderIds, query.trim().length > 0);

  const visibleOrders = useMemo(() => {
    const searched = orders ? searchPurchaseOrders(orders, searchLines, query) : [];
    return sort ? sortPurchaseOrders(searched, sort.key, sort.direction) : searched;
  }, [orders, searchLines, query, sort]);

  function toggleSort(key: OrderSortKey) {
    setSort((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: key === 'vendor' ? 'asc' : 'desc' };
    });
  }

  if (!enabled) {
    return (
      <AppShell>
        <main className="max-w-3xl mx-auto px-8 py-16">
          <Card>
            <p className="text-sm text-fg-secondary">
              The Orders module isn't enabled for this workspace.{' '}
              <Link to="/app/brands" className="text-accent-fg hover:underline">
                Back to Brands
              </Link>
            </p>
          </Card>
        </main>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <header className="border-b border-border-subtle">
        <div className="max-w-3xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <div>
            <h1 className="text-xl font-medium leading-tight">Orders</h1>
            <p className="text-xs text-fg-tertiary">
              {orders ? `${orders.length} purchase order${orders.length === 1 ? '' : 's'}` : ' '}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isOwner && (
              <Dialog open={applyingRule} onOpenChange={setApplyingRule}>
                <Button
                  variant="secondary"
                  leadingIcon={<CreditCard className="size-4" />}
                  onClick={() => setApplyingRule(true)}
                >
                  Apply card surcharge
                </Button>
                <DialogContent className="max-w-2xl">
                  <ApplyCardSurchargeForm
                    orders={orders ?? []}
                    onClose={() => setApplyingRule(false)}
                  />
                </DialogContent>
              </Dialog>
            )}
            <Dialog open={creating} onOpenChange={setCreating}>
              <Button leadingIcon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                New purchase order
              </Button>
              <DialogContent className="max-w-2xl">
                <CreatePoForm onClose={() => setCreating(false)} />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-8 py-8 space-y-3">
        {warehouseEnabled && <CreditClaimsSection />}
        {isLoading && <OrderCardSkeletons />}
        {isError && <p className="text-sm text-negative">Could not load orders.</p>}
        {orders?.length === 0 && (
          <Card>
            <p className="text-sm text-fg-secondary">
              No purchase orders yet. KeHE invoices land here automatically once ingestion runs, or
              create a manual order above.
            </p>
          </Card>
        )}
        {orders && orders.length > 0 && (
          <>
            <div className="relative max-w-sm">
              <Search
                className="h-4 w-4 text-fg-tertiary absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                aria-hidden="true"
              />
              <Input
                inputSize="sm"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search vendor, invoice #, or item..."
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-fg-tertiary mr-0.5">Sort:</span>
              <SortButton
                label="Date"
                active={sort?.key === 'orderedAt'}
                direction={sort?.key === 'orderedAt' ? sort.direction : 'desc'}
                onClick={() => toggleSort('orderedAt')}
              />
              <SortButton
                label="Vendor"
                active={sort?.key === 'vendor'}
                direction={sort?.key === 'vendor' ? sort.direction : 'asc'}
                onClick={() => toggleSort('vendor')}
              />
              <SortButton
                label="Total"
                active={sort?.key === 'total'}
                direction={sort?.key === 'total' ? sort.direction : 'desc'}
                onClick={() => toggleSort('total')}
              />
              <SortButton
                label="Status"
                active={sort?.key === 'status'}
                direction={sort?.key === 'status' ? sort.direction : 'desc'}
                onClick={() => toggleSort('status')}
              />
            </div>
            {visibleOrders.length === 0 && (
              <Card>
                <p className="text-sm text-fg-secondary">No orders match "{query}".</p>
              </Card>
            )}
          </>
        )}
        {visibleOrders.map((po) => {
          const badge = STATUS_BADGE[po.status];
          const open = openId === po.id;
          const credit = po.docType === 'credit_memo';
          return (
            <Card key={po.id}>
              <button
                type="button"
                onClick={() => setOpenId(open ? null : po.id)}
                className="w-full flex items-center justify-between gap-3 text-left"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-fg-primary">
                    {po.vendor}
                    {po.externalId && (
                      <span className="font-mono text-fg-tertiary"> #{po.externalId}</span>
                    )}
                    {credit && <span className="text-fg-tertiary"> (credit memo)</span>}
                  </p>
                  <p className="text-xs text-fg-tertiary">
                    {po.orderedAt ? new Date(po.orderedAt).toLocaleDateString() : 'no date'} ·{' '}
                    {po.lineCount} line{po.lineCount === 1 ? '' : 's'}
                    {!po.lineItemsVerified && ' · lines unverified'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-sm font-medium ${credit ? 'text-positive' : ''}`}>
                    {money(po.totalCents)}
                  </span>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </div>
              </button>
              {open && <OrderLines id={po.id} orders={orders ?? []} canReview={canReview} />}
            </Card>
          );
        })}
      </main>
    </AppShell>
  );
}

/** Skeleton stand-in for the PO card list while `usePurchaseOrders` loads —
 *  shape mirrors the real `<Card>` row (title + subtitle on the left, price
 *  + status badge on the right). */
function OrderCardSkeletons() {
  return (
    <>
      {Array.from({ length: 3 }, (_, i) => (
        <Card key={i}>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-1.5">
              <SkeletonLine className="w-40" />
              <SkeletonLine className="w-28 h-3" />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <SkeletonLine className="w-14" />
              <Skeleton className="h-5 w-20 rounded-xs" />
            </div>
          </div>
        </Card>
      ))}
    </>
  );
}

/** Toolbar sort control (card-list equivalent of AmazonPage's SortableTh —
 *  these pages use a Card list, not a table, so sorting is a button row
 *  rather than clickable column headers). */
function SortButton({
  label,
  active,
  direction,
  onClick,
}: {
  label: string;
  active: boolean;
  direction: 'asc' | 'desc';
  onClick: () => void;
}) {
  const Icon = active ? (direction === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-sm border transition-colors duration-fast',
        active
          ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
          : 'border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
      )}
    >
      {label}
      <Icon className="h-3 w-3" aria-hidden="true" />
    </button>
  );
}

export function OrderLines({
  id,
  orders,
  canReview = false,
}: {
  id: string;
  orders: PurchaseOrderSummary[];
  /** R2 item 9 fix pass I7: owner/operator only — same bar the PATCH
   *  .../lines/:lineId route enforces. Defaults false so existing
   *  call sites (and tests) that don't pass it render no Review UI. */
  canReview?: boolean;
}) {
  const { data, isLoading } = usePurchaseOrder(id);
  const advance = useAdvancePoStatus();
  const [advanceError, setAdvanceError] = useState<string | null>(null);
  const [reviewingLineId, setReviewingLineId] = useState<string | null>(null);
  if (isLoading) return <OrderLinesSkeleton />;
  if (!data) return null;
  const next = nextPoStatus(data.status);
  const reviewingLine = data.lines.find((l) => l.id === reviewingLineId) ?? null;
  async function handleAdvance() {
    if (!next) return;
    setAdvanceError(null);
    try {
      await advance.mutateAsync({ id, status: next });
    } catch (err) {
      setAdvanceError(err instanceof ApiCallError ? err.message : 'Could not advance status.');
    }
  }
  return (
    <div className="mt-3 border-t border-border-subtle pt-2">
      <ul className="divide-y divide-border-subtle">
        {data.lines.map((l) => (
          <li key={l.id} className="py-2 flex items-baseline justify-between gap-3 text-sm">
            <div className="flex items-start gap-3 min-w-0">
              <ProductThumb imageUrl={l.imageUrl} alt={l.description} />
              <div className="min-w-0">
                <p className="text-fg-primary truncate">{l.description}</p>
                {l.productName != null && (
                  <p className="text-xs text-fg-secondary truncate">
                    <ProductLink asin={l.asin}>{l.productName}</ProductLink>
                  </p>
                )}
                <p className="text-xs text-fg-tertiary font-mono">
                  {l.itemNo && `item ${l.itemNo}`}
                  {l.itemNo && l.upc && ' · '}
                  {l.upc && `UPC ${l.upc}`}
                </p>
                {l.reviewReason != null && (
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <Badge variant="warning">Needs review: {l.reviewReason}</Badge>
                    {canReview && (
                      <button
                        type="button"
                        onClick={() => setReviewingLineId(l.id)}
                        className="text-xs text-accent-fg hover:underline"
                      >
                        Review
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-fg-primary">{money(l.lineTotalCents)}</p>
              <p className="text-xs text-fg-tertiary">
                {l.qty} ×{' '}
                {l.grossUnitCostCents != null ? (
                  <>
                    <span className="line-through mr-1">{money(l.grossUnitCostCents)}</span>
                    {money(l.unitCostCents)}
                  </>
                ) : (
                  money(l.unitCostCents)
                )}
              </p>
              {/* R2 item 5 (returned to vendor): per-line returned units. */}
              {l.unitsReturned > 0 && (
                <p className="text-xs text-fg-tertiary">
                  Returned: {l.unitsReturned} unit{l.unitsReturned === 1 ? '' : 's'}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
      <ChargesSection
        poId={id}
        vendor={data.vendor}
        orders={orders}
        lots={data.lots ?? []}
        returns={data.returns ?? []}
      />
      <p className="mt-2 text-xs text-fg-tertiary text-right">
        {data.landedSummary
          ? landedSummaryLine(
              { ...data.landedSummary, allowanceCents: data.allowanceTotalCents },
              'USD'
            )
          : `Subtotal ${money(data.subtotalCents)}${
              data.allowanceTotalCents != null && data.allowanceTotalCents > 0
                ? ` · Allowances −${money(data.allowanceTotalCents)}`
                : ''
            }${data.feesCents !== 0 ? ` · fees ${money(data.feesCents)}` : ''} · total ${money(data.totalCents)}`}
      </p>
      {next && (
        <div className="mt-2 flex items-center justify-end gap-2">
          {advanceError && <p className="text-xs text-negative">{advanceError}</p>}
          <Button size="sm" variant="secondary" loading={advance.isPending} onClick={handleAdvance}>
            Advance to {STATUS_BADGE[next].label}
          </Button>
        </div>
      )}
      {canReview && (
        <Dialog
          open={reviewingLine != null}
          onOpenChange={(open) => !open && setReviewingLineId(null)}
        >
          <DialogContent>
            {reviewingLine && (
              <ReviewHoldForm
                poId={id}
                line={reviewingLine}
                onClose={() => setReviewingLineId(null)}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/** R2 item 9 (2026-09-03 intake sanity checks) fix pass I7: the "Review"
 *  popover's body — editable qty / unit cost / units per case, defaulted
 *  to the line's current (as-ingested) values, and a "Clear hold" button
 *  that calls PATCH /api/orders/:id/lines/:lineId. lineTotalCents is
 *  deliberately not editable here (see UpdatePurchaseOrderLineRequest's
 *  doc comment — it's the reconciliation anchor, not a correction target). */
function ReviewHoldForm({
  poId,
  line,
  onClose,
}: {
  poId: string;
  line: PurchaseOrderLine;
  onClose: () => void;
}) {
  const clearReview = useClearReviewForPoLine();
  const [qty, setQty] = useState(String(line.qty));
  const [unitCost, setUnitCost] = useState((line.unitCostCents / 100).toFixed(2));
  const [unitsPerCase, setUnitsPerCase] = useState(
    line.unitsPerCase != null ? String(line.unitsPerCase) : ''
  );
  const [error, setError] = useState<string | null>(null);

  async function handleClearHold() {
    setError(null);
    const qtyNum = Number(qty);
    const unitCostCentsNum = Math.round(Number(unitCost) * 100);
    const unitsPerCaseNum = unitsPerCase.trim() ? Number(unitsPerCase) : null;
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
      setError('Qty must be a positive number.');
      return;
    }
    if (!Number.isFinite(unitCostCentsNum) || unitCostCentsNum <= 0) {
      setError('Unit cost must be a positive amount.');
      return;
    }
    if (unitsPerCaseNum != null && (!Number.isFinite(unitsPerCaseNum) || unitsPerCaseNum <= 0)) {
      setError('Units per case must be a positive number, or left blank.');
      return;
    }
    try {
      await clearReview.mutateAsync({
        poId,
        lineId: line.id,
        qty: qtyNum,
        unitCostCents: unitCostCentsNum,
        unitsPerCase: unitsPerCaseNum,
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Could not clear the hold.');
    }
  }

  return (
    <div>
      <DialogHeader>
        <DialogTitle>Review hold</DialogTitle>
        <DialogDescription>{line.description}</DialogDescription>
      </DialogHeader>
      <p className="text-sm text-fg-secondary mt-1">{line.reviewReason}</p>
      <div className="flex gap-3 flex-wrap mt-3">
        <FormField label="Qty" htmlFor="review-qty" className="w-24">
          <Input
            id="review-qty"
            type="number"
            min={1}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </FormField>
        <FormField label="Unit cost ($)" htmlFor="review-unit-cost" className="w-28">
          <Input
            id="review-unit-cost"
            type="number"
            min={0}
            step={0.01}
            value={unitCost}
            onChange={(e) => setUnitCost(e.target.value)}
          />
        </FormField>
        <FormField label="Units per case" htmlFor="review-units-per-case" className="w-32">
          <Input
            id="review-units-per-case"
            type="number"
            min={1}
            placeholder="—"
            value={unitsPerCase}
            onChange={(e) => setUnitsPerCase(e.target.value)}
          />
        </FormField>
      </div>
      {error && <p className="text-xs text-negative mt-2">{error}</p>}
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose} disabled={clearReview.isPending}>
          Cancel
        </Button>
        <Button type="button" loading={clearReview.isPending} onClick={handleClearHold}>
          Clear hold
        </Button>
      </DialogFooter>
    </div>
  );
}

/** Skeleton stand-in for `OrderLines` while `usePurchaseOrder` loads — one
 *  placeholder per line item, shaped like the real thumb + description +
 *  qty/price row. */
function OrderLinesSkeleton() {
  return (
    <div className="mt-3 border-t border-border-subtle pt-2">
      <ul className="divide-y divide-border-subtle">
        {Array.from({ length: 3 }, (_, i) => (
          <li key={i} className="py-2 flex items-baseline justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <Skeleton className="h-12 w-12 rounded-md shrink-0" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <SkeletonLine className="w-48" />
                <SkeletonLine className="w-24 h-3" />
              </div>
            </div>
            <div className="text-right shrink-0 space-y-1.5">
              <SkeletonLine className="w-16 ml-auto" />
              <SkeletonLine className="w-12 h-3 ml-auto" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Charges & credits (R1 item 5, 2026-09-01 design doc) — the landed-cost
// ledger for a single PO: fuel/card surcharges, freight, other fees, and
// credit memos. Own fetch (usePoCharges) at a dedicated query key, distinct
// from the PO detail/lines fetch above.
// ─────────────────────────────────────────────────────────

const CHARGE_SOURCE_BADGE: Partial<Record<PoCharge['source'], string>> = {
  invoice_parse: 'From invoice',
  rule: 'By rule',
};

function ChargeRow({
  charge,
  canMarkReturned,
  onDeleteRequest,
  onMarkReturnedRequest,
}: {
  charge: PoCharge;
  canMarkReturned: boolean;
  onDeleteRequest: () => void;
  onMarkReturnedRequest: () => void;
}) {
  const negative = charge.amountCents < 0;
  const sourceLabel = CHARGE_SOURCE_BADGE[charge.source];
  return (
    <li className="py-2 flex items-center justify-between gap-3 text-sm">
      <div className="min-w-0">
        <p className="text-fg-primary">
          {chargeKindLabel(charge.kind)}
          {charge.externalId && (
            <span className="font-mono text-fg-tertiary"> #{charge.externalId}</span>
          )}
        </p>
        <p className="text-xs text-fg-tertiary">
          {new Date(charge.chargedAt).toLocaleDateString()}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={negative ? 'text-negative' : 'text-fg-primary'}>
          {money(charge.amountCents)}
        </span>
        <Badge variant={charge.treatment === 'landed_cost' ? 'positive' : 'neutral'}>
          {charge.treatment === 'landed_cost' ? 'Landed cost' : 'Cash only'}
        </Badge>
        {sourceLabel && <Badge variant="info">{sourceLabel}</Badge>}
        {/* R2 item 5 (returned to vendor): only credit memos get the
         *  "Mark goods returned" action — every other charge kind has no
         *  goods to return against. */}
        {canMarkReturned && charge.kind === 'credit_memo' && (
          <Button variant="ghost" size="sm" onClick={onMarkReturnedRequest}>
            Mark goods returned
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onDeleteRequest}>
          Delete
        </Button>
      </div>
    </li>
  );
}

// ─────────────────────────────────────────────────────────
// "Mark goods returned" (R2 item 5, 2026-09-03 design doc) — booked from a
// credit memo row: pick the lot, quantity, date (defaults to the memo's
// chargedAt), and an optional note.
// ─────────────────────────────────────────────────────────

const ReturnFormValues = z.object({
  lotId: z.string().min(1, 'Select a lot'),
  qty: z.string().min(1, 'Quantity is required'),
  returnedAt: z.string().min(1, 'Date is required'),
  note: z.string().max(500),
});
type ReturnFormValues = z.infer<typeof ReturnFormValues>;

function lotLabel(lot: PoLot): string {
  return `${lot.itemNo ? `Item ${lot.itemNo}` : `Lot ${lot.id.slice(0, 8)}`} — ${lot.availableToReturn} available (received ${lot.qtyReceived}, returned ${lot.qtyReturned})`;
}

function MarkReturnedDialog({
  poId,
  charge,
  lots,
  onClose,
}: {
  poId: string;
  charge: PoCharge | null;
  lots: PoLot[];
  onClose: () => void;
}) {
  const createReturn = useCreateLotReturn(poId);
  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReturnFormValues>({
    resolver: zodResolver(ReturnFormValues),
    defaultValues: { lotId: '', qty: '', returnedAt: todayDateInput(), note: '' },
  });
  const lotId = watch('lotId');
  const qty = watch('qty');
  const returnedAt = watch('returnedAt');
  const note = watch('note');
  const selectedLot = lots.find((l) => l.id === lotId) ?? null;
  const qtyNumber = Number(qty);
  const overAvailable =
    selectedLot != null && Number.isFinite(qtyNumber) && qtyNumber > selectedLot.availableToReturn;

  useEffect(() => {
    if (charge) {
      reset({
        lotId: '',
        qty: '',
        // Local calendar date (S2 fix), not chargedAt.slice(0, 10) (UTC) —
        // see localDateInput's doc comment.
        returnedAt: localDateInput(new Date(charge.chargedAt)),
        note: '',
      });
    }
  }, [charge, reset]);

  if (!charge) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark goods returned</DialogTitle>
          <DialogDescription>
            Record units sent back to the vendor against this credit memo.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={handleSubmit(async (values) => {
            try {
              await createReturn.mutateAsync({
                lotId: values.lotId,
                qty: Number(values.qty),
                returnedAt: values.returnedAt,
                poChargeId: charge.id,
                note: values.note.trim() || undefined,
              });
              onClose();
            } catch {
              // surfaced via createReturn.isError below
            }
          })}
        >
          <FormField label="Lot" htmlFor="return-lot" error={errors.lotId?.message}>
            <Select value={lotId} onValueChange={(v) => setValue('lotId', v)}>
              <SelectTrigger id="return-lot">
                <SelectValue placeholder="Select a lot" />
              </SelectTrigger>
              <SelectContent>
                {lots.map((lot) => (
                  <SelectItem key={lot.id} value={lot.id}>
                    {lotLabel(lot)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <div className="grid gap-2 sm:grid-cols-2">
            <FormField
              label="Quantity"
              htmlFor="return-qty"
              error={errors.qty?.message ?? (overAvailable ? 'Exceeds available units' : undefined)}
            >
              <Input
                id="return-qty"
                inputSize="sm"
                inputMode="numeric"
                value={qty}
                onChange={(e) => setValue('qty', e.target.value)}
                aria-invalid={errors.qty || overAvailable ? true : undefined}
              />
            </FormField>
            <FormField label="Date" htmlFor="return-date" error={errors.returnedAt?.message}>
              <Input
                id="return-date"
                type="date"
                inputSize="sm"
                value={returnedAt}
                onChange={(e) => setValue('returnedAt', e.target.value)}
              />
            </FormField>
          </div>
          <FormField label="Note" htmlFor="return-note">
            <Input
              id="return-note"
              inputSize="sm"
              value={note}
              onChange={(e) => setValue('note', e.target.value)}
            />
          </FormField>
          {createReturn.isError && (
            <p className="text-xs text-negative">
              {createReturn.error instanceof ApiCallError
                ? createReturn.error.message
                : 'Could not record the return.'}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              variant="secondary"
              loading={isSubmitting || createReturn.isPending}
              disabled={overAvailable}
            >
              Mark returned
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Shimmer stand-in for the charges list while `usePoCharges` loads. */
function ChargesSkeleton() {
  return (
    <ul className="space-y-2" aria-hidden="true">
      {Array.from({ length: 2 }, (_, i) => (
        <li key={i} className="flex items-center justify-between gap-3 py-1">
          <SkeletonLine className="w-40" />
          <SkeletonLine className="w-20" />
        </li>
      ))}
    </ul>
  );
}

/** YYYY-MM-DD for a `<input type="date">` value, using the browser's LOCAL
 *  calendar date — matches how ChargeRow displays chargedAt via the
 *  default (local) `toLocaleDateString()`, so a date-input default agrees
 *  with the date the user just saw rendered. `date.toISOString().slice(0,
 *  10)` (the previous implementation) reads the UTC calendar date instead,
 *  which can land a day off from local for a timestamp with a real
 *  time-of-day near a local midnight boundary. */
function localDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function todayDateInput(): string {
  return localDateInput(new Date());
}

const ChargeFormValues = z
  .object({
    kind: PoChargeKind,
    amount: z.string().min(1, 'Amount is required'),
    chargedAt: z.string().min(1, 'Date is required'),
    treatment: PoChargeTreatment,
    externalId: z.string().max(64),
    appliedToPoId: z.string(),
  })
  .refine((v) => signedAmountForKind(v.kind, v.amount) !== null, {
    message: 'Enter a positive dollar amount',
    path: ['amount'],
  });
type ChargeFormValues = z.infer<typeof ChargeFormValues>;

function emptyChargeFormValues(): ChargeFormValues {
  return {
    kind: 'fuel_surcharge',
    amount: '',
    chargedAt: todayDateInput(),
    treatment: defaultTreatmentForKind('fuel_surcharge'),
    externalId: '',
    appliedToPoId: '',
  };
}

/** Inline "Add" form under the charges list — dollars in, cents out (D1).
 *  Controlled via watch/setValue rather than register: react-test-renderer
 *  has no DOM, so register's ref-based wiring never fires there. */
function AddChargeForm({
  poId,
  vendor,
  orders,
  createCharge,
}: {
  poId: string;
  vendor: string;
  orders: PurchaseOrderSummary[];
  createCharge: ReturnType<typeof useCreatePoCharge>;
}) {
  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChargeFormValues>({
    resolver: zodResolver(ChargeFormValues),
    defaultValues: emptyChargeFormValues(),
  });

  const kind = watch('kind');
  const amount = watch('amount');
  const chargedAt = watch('chargedAt');
  const treatment = watch('treatment');
  const externalId = watch('externalId');
  const appliedToPoId = watch('appliedToPoId');
  const isCredit = kind === 'credit_memo';
  const invoiceOptions = isCredit ? vendorInvoiceOptions(orders, vendor, poId) : [];

  function handleKindChange(next: PoChargeKind) {
    setValue('kind', next);
    setValue('treatment', defaultTreatmentForKind(next));
  }

  return (
    <form
      className="mt-3 space-y-3"
      onSubmit={handleSubmit(async (values) => {
        const amountCents = signedAmountForKind(values.kind, values.amount);
        if (amountCents == null) return;
        try {
          await createCharge.mutateAsync({
            kind: values.kind,
            treatment: values.treatment,
            amountCents,
            chargedAt: values.chargedAt,
            externalId: values.externalId.trim() || undefined,
            appliedToPoId:
              values.kind === 'credit_memo' && values.appliedToPoId
                ? values.appliedToPoId
                : undefined,
          });
          reset(emptyChargeFormValues());
        } catch {
          // surfaced via createCharge.isError below
        }
      })}
    >
      <div className="grid gap-2 sm:grid-cols-4">
        <FormField label="Kind" htmlFor="charge-kind">
          <Select value={kind} onValueChange={(v) => handleKindChange(v as PoChargeKind)}>
            <SelectTrigger id="charge-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PoChargeKind.options.map((k) => (
                <SelectItem key={k} value={k}>
                  {chargeKindLabel(k)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Amount" htmlFor="charge-amount" error={errors.amount?.message}>
          <Input
            id="charge-amount"
            inputSize="sm"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setValue('amount', e.target.value)}
            placeholder="0.00"
            aria-invalid={errors.amount ? true : undefined}
          />
        </FormField>
        <FormField label="Date" htmlFor="charge-date">
          <Input
            id="charge-date"
            type="date"
            inputSize="sm"
            value={chargedAt}
            onChange={(e) => setValue('chargedAt', e.target.value)}
          />
        </FormField>
        <FormField label="Treatment" htmlFor="charge-treatment" required>
          <Select
            value={treatment}
            onValueChange={(v) => setValue('treatment', v as PoChargeTreatment)}
          >
            <SelectTrigger id="charge-treatment">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="landed_cost">Landed cost</SelectItem>
              <SelectItem value="cash_only">Cash only</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
      </div>
      {isCredit && (
        <div className="grid gap-2 sm:grid-cols-2">
          <FormField label="Memo #" htmlFor="charge-external-id">
            <Input
              id="charge-external-id"
              inputSize="sm"
              value={externalId}
              onChange={(e) => setValue('externalId', e.target.value)}
            />
          </FormField>
          <FormField label="Applied to invoice" htmlFor="charge-applied-to">
            <Select value={appliedToPoId} onValueChange={(v) => setValue('appliedToPoId', v)}>
              <SelectTrigger id="charge-applied-to">
                <SelectValue placeholder="Select an invoice" />
              </SelectTrigger>
              <SelectContent>
                {invoiceOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>
      )}
      {createCharge.isError && (
        <p className="text-xs text-negative">
          {createCharge.error instanceof ApiCallError
            ? createCharge.error.message
            : 'Could not save the charge.'}
        </p>
      )}
      <Button
        type="submit"
        size="sm"
        variant="secondary"
        loading={isSubmitting || createCharge.isPending}
      >
        Add
      </Button>
    </form>
  );
}

// R2 item 5 (S2): one row per active return, rendered under Charges &
// credits — "Returned {qty} units · {date} · memo {externalId} · {note}".
function ReturnRow({
  ret,
  memoExternalId,
  canReverse,
  onReverseRequest,
}: {
  ret: LotReturn;
  memoExternalId: string | null;
  canReverse: boolean;
  onReverseRequest: () => void;
}) {
  const parts = [
    `Returned ${ret.qty} unit${ret.qty === 1 ? '' : 's'}`,
    formatFinanceDate(ret.returnedAt),
    memoExternalId ? `memo ${memoExternalId}` : null,
    ret.note?.trim() || null,
  ].filter((p): p is string => Boolean(p));
  return (
    <li className="py-2 flex items-center justify-between gap-3 text-sm">
      <p className="text-fg-primary min-w-0 truncate">{parts.join(' · ')}</p>
      {canReverse && (
        <Button variant="ghost" size="sm" className="shrink-0" onClick={onReverseRequest}>
          Reverse
        </Button>
      )}
    </li>
  );
}

function ChargesSection({
  poId,
  vendor,
  orders,
  lots,
  returns,
}: {
  poId: string;
  vendor: string;
  orders: PurchaseOrderSummary[];
  lots: PoLot[];
  returns: LotReturn[];
}) {
  const { data, isLoading } = usePoCharges(poId);
  const createCharge = useCreatePoCharge(poId);
  const deleteCharge = useDeletePoCharge(poId);
  const deleteReturn = useDeleteLotReturn(poId);
  const { data: me } = useMe();
  // R2 item 5: recording a return is owner OR operator (unlike vendor
  // charges above, which stay owner-only). Reversing one is gated the same
  // way.
  const canMarkReturned = me?.user.role === 'owner' || me?.user.role === 'operator';
  // R2 item 1 (D5) — read-only: a PO charge write only marks the tenant's
  // COGS replan pending now (it no longer reconciles inline), so this note
  // says the nightly batch (or "Recompute now" on Finance) hasn't landed
  // this charge's cost effect into landed COGS yet.
  const cogsReplanQuery = useCogsReplanState(true);
  const cogsRecomputePendingCount = cogsReplanQuery.data?.state.pendingCount ?? 0;
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [returnCharge, setReturnCharge] = useState<PoCharge | null>(null);
  const [pendingReverseId, setPendingReverseId] = useState<string | null>(null);
  const [reverseError, setReverseError] = useState<string | null>(null);

  const chargeExternalIdById = new Map((data?.charges ?? []).map((c) => [c.id, c.externalId]));

  async function confirmDelete() {
    if (!pendingDeleteId) return;
    setDeleteError(null);
    try {
      await deleteCharge.mutateAsync(pendingDeleteId);
      setPendingDeleteId(null);
    } catch (err) {
      setDeleteError(err instanceof ApiCallError ? err.message : 'Could not delete the charge.');
    }
  }

  async function confirmReverse() {
    if (!pendingReverseId) return;
    setReverseError(null);
    try {
      await deleteReturn.mutateAsync(pendingReverseId);
      setPendingReverseId(null);
    } catch (err) {
      setReverseError(err instanceof ApiCallError ? err.message : 'Could not reverse the return.');
    }
  }

  return (
    <div className="mt-3 border-t border-border-subtle pt-3">
      <p className="text-sm font-medium text-fg-primary mb-2">Charges &amp; credits</p>
      {cogsRecomputePendingCount > 0 && (
        <p className="text-xs text-fg-tertiary mb-2">COGS recompute pending (nightly)</p>
      )}
      {isLoading && <ChargesSkeleton />}
      {!isLoading && data && data.charges.length === 0 && (
        <p className="text-xs text-fg-tertiary">No charges or credits recorded.</p>
      )}
      {!isLoading && data && data.charges.length > 0 && (
        <ul className="divide-y divide-border-subtle">
          {data.charges.map((c) => (
            <ChargeRow
              key={c.id}
              charge={c}
              canMarkReturned={canMarkReturned}
              onDeleteRequest={() => setPendingDeleteId(c.id)}
              onMarkReturnedRequest={() => setReturnCharge(c)}
            />
          ))}
        </ul>
      )}
      {returns.length > 0 && (
        <ul className="mt-2 divide-y divide-border-subtle border-t border-border-subtle">
          {returns.map((ret) => (
            <ReturnRow
              key={ret.id}
              ret={ret}
              memoExternalId={
                ret.poChargeId ? (chargeExternalIdById.get(ret.poChargeId) ?? null) : null
              }
              canReverse={canMarkReturned}
              onReverseRequest={() => setPendingReverseId(ret.id)}
            />
          ))}
        </ul>
      )}
      <AddChargeForm poId={poId} vendor={vendor} orders={orders} createCharge={createCharge} />
      <Dialog
        open={pendingDeleteId != null}
        onOpenChange={(open) => !open && setPendingDeleteId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this charge?</DialogTitle>
            <DialogDescription>This cannot be undone.</DialogDescription>
          </DialogHeader>
          {deleteError && <p className="text-sm text-negative">{deleteError}</p>}
          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPendingDeleteId(null)}
              disabled={deleteCharge.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              loading={deleteCharge.isPending}
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={pendingReverseId != null}
        onOpenChange={(open) => !open && setPendingReverseId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reverse this return?</DialogTitle>
            <DialogDescription>
              Restores the units to on-hand and re-spreads any landed-cost charges on this PO.
            </DialogDescription>
          </DialogHeader>
          {reverseError && <p className="text-sm text-negative">{reverseError}</p>}
          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPendingReverseId(null)}
              disabled={deleteReturn.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              loading={deleteReturn.isPending}
              onClick={confirmReverse}
            >
              Reverse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MarkReturnedDialog
        poId={poId}
        charge={returnCharge}
        lots={lots}
        onClose={() => setReturnCharge(null)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Apply card surcharge (D6) — preview + apply dialog, KeHE-shaped but not
// vendor-locked. Apply is gated on having previewed first: the button's
// count comes from the preview response, so there's no "apply blind" path.
// ─────────────────────────────────────────────────────────

const RULE_STATUS_LABEL: Record<ApplyChargeRuleRow['status'], string> = {
  created: 'Created',
  exists: 'Exists',
  skipped_no_base: 'Skipped',
};

function RuleRow({ row }: { row: ApplyChargeRuleRow }) {
  return (
    <tr className="border-t border-border-subtle">
      <td className="py-1 pr-2 font-mono text-xs">{row.externalId ?? '—'}</td>
      <td className="py-1 pr-2 text-xs">
        {row.orderedAt ? new Date(row.orderedAt).toLocaleDateString() : '—'}
      </td>
      <td className="py-1 pr-2 text-right">{money(row.totalCents)}</td>
      <td className="py-1 pr-2 text-right">{money(row.creditsAppliedCents)}</td>
      <td className="py-1 pr-2 text-right">{money(row.baseCents)}</td>
      <td className="py-1 pr-2 text-right">{money(row.amountCents)}</td>
      <td className="py-1 text-right">
        <Badge
          variant={
            row.status === 'created' ? 'positive' : row.status === 'exists' ? 'neutral' : 'warning'
          }
        >
          {RULE_STATUS_LABEL[row.status]}
        </Badge>
      </td>
    </tr>
  );
}

export function ApplyCardSurchargeForm({
  orders,
  onClose,
}: {
  orders: PurchaseOrderSummary[];
  onClose: () => void;
}) {
  const [vendor, setVendor] = useState(() => mostCommonVendor(orders));
  const [ratePercent, setRatePercent] = useState('3');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [preview, setPreview] = useState<ApplyChargeRuleResponse | null>(null);
  const applyRule = useApplyChargeRule();

  function errorMessage(err: unknown, fallback: string): string {
    return err instanceof ApiCallError ? err.message : fallback;
  }

  async function handlePreview() {
    try {
      const res = await applyRule.mutateAsync({
        vendor: vendor.trim(),
        kind: 'card_surcharge',
        ratePercent: Number(ratePercent),
        from: from || undefined,
        to: to || undefined,
        apply: false,
      });
      setPreview(res);
    } catch {
      // surfaced via applyRule.isError below
    }
  }

  async function handleApply() {
    try {
      const res = await applyRule.mutateAsync({
        vendor: vendor.trim(),
        kind: 'card_surcharge',
        ratePercent: Number(ratePercent),
        from: from || undefined,
        to: to || undefined,
        apply: true,
      });
      setPreview(res);
      onClose();
    } catch {
      // surfaced via applyRule.isError below
    }
  }

  const createableCount = preview ? preview.rows.filter((r) => r.status === 'created').length : 0;
  const canRun = vendor.trim().length > 0 && Number(ratePercent) > 0 && !applyRule.isPending;

  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle>Apply card surcharge</DialogTitle>
        <DialogDescription>
          round(rate% × (invoice total − credits already applied)) on every matching invoice without
          an active card surcharge. Idempotent — a re-run shows existing charges as "Exists", never
          duplicates.
        </DialogDescription>
      </DialogHeader>

      <div className="flex gap-3 flex-wrap">
        <FormField label="Vendor" htmlFor="rule-vendor" required className="flex-1 min-w-40">
          <Input id="rule-vendor" value={vendor} onChange={(e) => setVendor(e.target.value)} />
        </FormField>
        <FormField label="Rate %" htmlFor="rule-rate" required className="w-24">
          <Input
            id="rule-rate"
            type="number"
            min={0}
            max={10}
            step={0.1}
            value={ratePercent}
            onChange={(e) => setRatePercent(e.target.value)}
          />
        </FormField>
        <FormField label="From" htmlFor="rule-from" className="w-36">
          <Input
            id="rule-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </FormField>
        <FormField label="To" htmlFor="rule-to" className="w-36">
          <Input id="rule-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </FormField>
      </div>

      {applyRule.isError && (
        <p className="text-sm text-negative">
          {errorMessage(applyRule.error, 'Could not run the rule.')}
        </p>
      )}

      {preview && (
        <div className="space-y-2">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-fg-tertiary text-left">
                  <th className="py-1 pr-2">Invoice #</th>
                  <th className="py-1 pr-2">Date</th>
                  <th className="py-1 pr-2 text-right">Total</th>
                  <th className="py-1 pr-2 text-right">Credits applied</th>
                  <th className="py-1 pr-2 text-right">Base</th>
                  <th className="py-1 pr-2 text-right">Surcharge</th>
                  <th className="py-1 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <RuleRow key={row.poId} row={row} />
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-fg-secondary text-right">
            Total {money(chargeRuleTotal(preview.rows))}
          </p>
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose} disabled={applyRule.isPending}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="secondary"
          loading={applyRule.isPending && preview == null}
          disabled={!canRun}
          onClick={handlePreview}
        >
          Preview
        </Button>
        <Button
          type="button"
          loading={applyRule.isPending && preview != null}
          disabled={!canRun || !preview || createableCount === 0}
          onClick={handleApply}
        >
          Apply {createableCount}
        </Button>
      </DialogFooter>
    </div>
  );
}

const CLAIM_STATUS_BADGE: Record<
  'draft' | 'filed' | 'credited' | 'dismissed',
  { label: string; variant: 'warning' | 'info' | 'positive' | 'neutral' }
> = {
  draft: { label: 'Draft — file with vendor', variant: 'warning' },
  filed: { label: 'Filed', variant: 'info' },
  credited: { label: 'Credited', variant: 'positive' },
  dismissed: { label: 'Dismissed', variant: 'neutral' },
};

function CreditClaimsSection() {
  const { data: claims } = useCreditClaims();
  const update = useUpdateClaimStatus();
  const [openId, setOpenId] = useState<string | null>(null);
  const active = (claims?.filter((c) => c.status === 'draft' || c.status === 'filed') ?? []).sort(
    compareClaimDeadlines
  );
  if (active.length === 0) return null;
  const now = new Date();
  return (
    <Card className="border-warning/30">
      <p className="text-sm font-medium text-fg-primary mb-2">
        Credit claims ({active.length} open — {money(active.reduce((s, c) => s + c.amountCents, 0))}{' '}
        recoverable)
      </p>
      <ul className="divide-y divide-border-subtle -mx-4">
        {active.map((claim) => {
          const badge = CLAIM_STATUS_BADGE[claim.status];
          // Filing deadline only bites while the claim is still a draft.
          const deadline =
            claim.status === 'draft' ? claimDeadlineState(claim.deadlineAt, now) : null;
          const open = openId === claim.id;
          return (
            <li key={claim.id} className="px-4 py-2.5">
              <button
                type="button"
                onClick={() => setOpenId(open ? null : claim.id)}
                className="w-full flex items-center justify-between gap-3 text-left"
              >
                <div className="min-w-0">
                  <p className="text-sm text-fg-primary">
                    {claim.kind === 'damage' ? 'Damaged units' : 'Short delivery'} — {claim.vendor}
                    {claim.poExternalId && (
                      <span className="font-mono text-fg-tertiary"> #{claim.poExternalId}</span>
                    )}
                  </p>
                  <p className="text-xs text-fg-tertiary">
                    {new Date(claim.createdAt).toLocaleDateString()} · {claim.lines.length} item
                    {claim.lines.length === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm font-medium">{money(claim.amountCents)}</span>
                  {deadline && (
                    <Badge variant={deadline.kind === 'overdue' ? 'negative' : 'warning'}>
                      {deadline.label}
                    </Badge>
                  )}
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </div>
              </button>
              {open && (
                <div className="mt-2 space-y-2">
                  <pre className="whitespace-pre-wrap font-mono text-xs text-fg-secondary bg-surface-elev rounded-md p-3">
                    {claim.draftBody}
                  </pre>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => navigator.clipboard.writeText(claim.draftBody)}
                    >
                      Copy claim text
                    </Button>
                    {claim.status === 'draft' && (
                      <Button
                        size="sm"
                        disabled={update.isPending}
                        onClick={() => update.mutate({ id: claim.id, status: 'filed' })}
                      >
                        Mark filed
                      </Button>
                    )}
                    {claim.status === 'filed' && (
                      <Button
                        size="sm"
                        disabled={update.isPending}
                        onClick={() => update.mutate({ id: claim.id, status: 'credited' })}
                      >
                        Mark credited
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={update.isPending}
                      onClick={() => {
                        if (confirm('Dismiss this claim permanently? It cannot be reopened.')) {
                          update.mutate({ id: claim.id, status: 'dismissed' });
                        }
                      }}
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function emptyPoLine(): PoLineDraft {
  return { description: '', itemNo: '', upc: '', qty: '1', cost: '' };
}

/**
 * "New purchase order" dialog body — for purchases with no invoice to
 * ingest (e.g. an ASIN bought direct outside KeHE). Two-call submit
 * (create, then optionally PATCH status → confirmed) reusing the existing
 * endpoints; no new API route. Row mechanics copy CreateShipmentForm
 * (above); field grid copies PlanningPage's AddLineForm.
 */
function CreatePoForm({ onClose }: { onClose: () => void }) {
  const [vendor, setVendor] = useState('');
  const [orderedAt, setOrderedAt] = useState('');
  const [expectedAt, setExpectedAt] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<PoLineDraft[]>([emptyPoLine()]);
  const [alreadyOrdered, setAlreadyOrdered] = useState(true);
  // Set only when create() succeeded but the confirm-PATCH failed — the PO
  // already exists, so this state offers retry-advance, never retry-create.
  const [needsRetry, setNeedsRetry] = useState<{ id: string; message: string } | null>(null);
  const create = useCreatePurchaseOrder();
  const advance = useAdvancePoStatus();

  function updateLine(i: number, field: keyof PoLineDraft, value: string) {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  }
  function addLine() {
    setLines((prev) => [...prev, emptyPoLine()]);
  }
  function removeLine(i: number) {
    setLines((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));
  }

  const validLines = toValidPoLines(lines);
  const totalCents = orderTotalCents(validLines);
  const canSubmit =
    vendor.trim().length > 0 && validLines.length > 0 && !create.isPending && !advance.isPending;

  function errorMessage(err: unknown, fallback: string): string {
    return err instanceof ApiCallError ? err.message : fallback;
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    let created: { id: string };
    try {
      created = await create.mutateAsync({
        vendor: vendor.trim(),
        orderedAt: orderedAt ? new Date(orderedAt).toISOString() : undefined,
        expectedAt: expectedAt ? new Date(expectedAt).toISOString() : undefined,
        notes: notes.trim() || undefined,
        lines: validLines,
      });
    } catch {
      return; // surfaced via create.error below
    }
    if (!alreadyOrdered) {
      onClose();
      return;
    }
    try {
      await advance.mutateAsync({ id: created.id, status: 'confirmed' });
      onClose();
    } catch (err) {
      setNeedsRetry({ id: created.id, message: errorMessage(err, 'Could not mark it confirmed.') });
    }
  }

  async function retryAdvance() {
    if (!needsRetry) return;
    try {
      await advance.mutateAsync({ id: needsRetry.id, status: 'confirmed' });
      onClose();
    } catch (err) {
      setNeedsRetry({
        id: needsRetry.id,
        message: errorMessage(err, 'Could not mark it confirmed.'),
      });
    }
  }

  if (needsRetry) {
    return (
      <div className="space-y-4">
        <DialogHeader>
          <DialogTitle>Purchase order created</DialogTitle>
          <DialogDescription>
            It's saved and already in the list, but marking it confirmed failed:{' '}
            {needsRetry.message}. It's still "Placed" — receiving needs it at "Confirmed" or later.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Leave as Placed
          </Button>
          <Button type="button" loading={advance.isPending} onClick={retryAdvance}>
            Retry — mark confirmed
          </Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle>New purchase order</DialogTitle>
        <DialogDescription>
          For purchases with no invoice to ingest — e.g. an ASIN bought direct, outside KeHE.
        </DialogDescription>
      </DialogHeader>

      <div className="flex gap-3 flex-wrap">
        <FormField label="Vendor" htmlFor="po-vendor" required className="flex-1 min-w-40">
          <Input
            id="po-vendor"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            autoFocus
          />
        </FormField>
        <FormField label="Ordered date" htmlFor="po-ordered-at" className="w-36">
          <Input
            id="po-ordered-at"
            type="date"
            value={orderedAt}
            onChange={(e) => setOrderedAt(e.target.value)}
          />
        </FormField>
        <FormField label="Expected date" htmlFor="po-expected-at" className="w-36">
          <Input
            id="po-expected-at"
            type="date"
            value={expectedAt}
            onChange={(e) => setExpectedAt(e.target.value)}
          />
        </FormField>
      </div>

      <FormField label="Notes" htmlFor="po-notes">
        <textarea
          id="po-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          maxLength={4000}
          className="w-full text-sm rounded-sm border border-border-subtle bg-canvas px-3 py-2 focus:outline-none focus:ring-1 focus:ring-accent-fg"
        />
      </FormField>

      <div className="space-y-2">
        <p className="text-sm font-medium text-fg-primary">Lines</p>
        {lines.map((l, i) => (
          <div key={i} className="grid gap-2 sm:grid-cols-[2fr,1fr,1fr,72px,96px,auto] items-end">
            <FormField label="Description" htmlFor={`po-desc-${i}`}>
              <Input
                id={`po-desc-${i}`}
                inputSize="sm"
                value={l.description}
                onChange={(e) => updateLine(i, 'description', e.target.value)}
                placeholder="Widget A case"
              />
            </FormField>
            <FormField label="Item #" htmlFor={`po-item-${i}`}>
              <Input
                id={`po-item-${i}`}
                inputSize="sm"
                value={l.itemNo}
                onChange={(e) => updateLine(i, 'itemNo', e.target.value)}
                title="Match the listing's vendor item number to link this purchase to Amazon shipments"
              />
            </FormField>
            <FormField label="UPC" htmlFor={`po-upc-${i}`}>
              <Input
                id={`po-upc-${i}`}
                inputSize="sm"
                value={l.upc}
                onChange={(e) => updateLine(i, 'upc', e.target.value)}
              />
            </FormField>
            <FormField label="Qty" htmlFor={`po-qty-${i}`}>
              <Input
                id={`po-qty-${i}`}
                inputSize="sm"
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={l.qty}
                onChange={(e) => updateLine(i, 'qty', e.target.value)}
              />
            </FormField>
            <FormField label="Unit cost" htmlFor={`po-cost-${i}`}>
              <Input
                id={`po-cost-${i}`}
                inputSize="sm"
                type="number"
                min={0}
                step={0.01}
                value={l.cost}
                onChange={(e) => updateLine(i, 'cost', e.target.value)}
                placeholder="$0.00"
              />
            </FormField>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={lines.length === 1}
              onClick={() => removeLine(i)}
            >
              Remove
            </Button>
          </div>
        ))}
        <Button type="button" variant="secondary" size="sm" onClick={addLine}>
          Add line
        </Button>
      </div>

      <p className="text-sm text-fg-secondary text-right">Total {money(totalCents)}</p>

      <label className="flex items-center gap-2 text-sm text-fg-secondary">
        <input
          type="checkbox"
          checked={alreadyOrdered}
          onChange={(e) => setAlreadyOrdered(e.target.checked)}
          className="size-4"
        />
        Already ordered — mark confirmed
      </label>

      {create.isError && (
        <p className="text-sm text-negative">
          {errorMessage(create.error, 'Could not create the purchase order.')}
        </p>
      )}

      <DialogFooter>
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          disabled={create.isPending || advance.isPending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          loading={create.isPending || advance.isPending}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          Create
        </Button>
      </DialogFooter>
    </div>
  );
}
